/**
 * PredictiveSearch (Dawn-style) + OEM merge
 * Fixes:
 * - abort previous in-flight requests on every new search
 * - ignore stale responses (older term finishing after newer)
 * - handle AbortError properly
 * - guard when primary results list is missing
 * - harden updateSearchForTerm() against empty/regex terms
 */

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;

    // Controller will be recreated per request
    this.abortController = null;

    this.searchTerm = '';
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();

    // If user edits earlier chars, remove old wrapper
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the "Search for..." text
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    // Use cache if exists (optional, but keeps UX snappy)
    const queryKey = this.searchTerm.replace(/\s+/g, '-').toLowerCase();
    const cached = this.cachedResults?.[queryKey];
    if (cached) {
      this.renderSearchResults(cached);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';

      // Abort any in-flight request
      if (this.abortController) this.abortController.abort();
      this.abortController = null;

      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        /*this.selectOption();*/
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (!currentButtonText) return;

    // If previousTerm is empty, don't attempt replacements
    if (!previousTerm) {
      // Optional: if Dawn's string contains something like "... for “TERM”"
      // you can set it explicitly here, but safest is to do nothing.
      return;
    }

    // Escape regex chars in previousTerm (treat it as literal)
    const escapedPrev = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(escapedPrev, 'g');

    const matches = currentButtonText.match(re);
    if (matches && matches.length > 1) return;

    searchForTextElement.innerText = currentButtonText.replace(re, newTerm);
  }

  getSearchResults(searchTerm) {
    // Abort any previous in-flight request (race condition fix)
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const queryAtStart = searchTerm;
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();

    this.setLiveRegionLoadingState();

    const normalizedSearchTerm = searchTerm.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

    const primaryUrl = `${routes.predictive_search_url}?q=${encodeURIComponent(
      searchTerm
    )}&section_id=predictive-search`;

    const oemUrl = `/search?view=oem&q=${encodeURIComponent(normalizedSearchTerm)}`;

    const primarySearchPromise = fetch(primaryUrl, { signal: this.abortController.signal }).then((response) => {
      if (!response.ok) throw new Error(response.status);
      return response.text();
    });

    const oemSearchPromise = fetch(oemUrl, { signal: this.abortController.signal }).then((response) => {
      if (!response.ok) throw new Error(response.status);
      return response.text();
    });

    Promise.all([primarySearchPromise, oemSearchPromise])
      .then(([primaryText, oemText]) => {
        // Stale response protection: ignore if the input changed since request started
        if (this.getQuery() !== queryAtStart) return;

        const primaryDoc = new DOMParser().parseFromString(primaryText, 'text/html');
        const primaryResultsList = primaryDoc.querySelector('#predictive-search-results-products-list');

        // If Dawn returned markup without the expected list, just render it as-is
        if (!primaryResultsList) {
          const fallbackContent = primaryDoc.body.innerHTML;
          this.allPredictiveSearchInstances.forEach((instance) => {
            instance.cachedResults[queryKey] = fallbackContent;
          });
          this.renderSearchResults(fallbackContent);
          return;
        }

        const oemDoc = new DOMParser().parseFromString(oemText, 'text/html');

        // OEM response items (your view=oem should output these)
        const oemListItems = Array.from(oemDoc.querySelectorAll('.predictive-search__list-item'));

        // Build set of product IDs already present in primary list
        const uniqueProductIds = new Set();
        Array.from(primaryResultsList.children).forEach((item) => {
          const productId = item.getAttribute('data-product-id');
          if (productId) uniqueProductIds.add(productId);
        });

        // Prepend OEM items that are not duplicates
        oemListItems.forEach((item) => {
          const productId = item.getAttribute('data-product-id');
          if (productId && !uniqueProductIds.has(productId)) {
            primaryResultsList.prepend(item);
            uniqueProductIds.add(productId);
          }
        });

        const combinedContent = primaryDoc.body.innerHTML;

        // Cache across all predictive search instances
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = combinedContent;
        });

        this.renderSearchResults(combinedContent);
      })
      .catch((error) => {
        // Abort is expected when user types quickly
        if (error?.name === 'AbortError') return;

        // Some environments throw DOMException with name AbortError too:
        if (error instanceof DOMException && error.name === 'AbortError') return;

        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countEl) this.setLiveRegionText(countEl.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
